
import React, { useEffect, useRef } from 'react';
import { CycleData, HormoneType } from '@/utils/cycle-tracking';
import { HormoneChartDisplay } from '../hormone-chart/HormoneChartDisplay';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ArrowLeftRight } from 'lucide-react';

interface HormoneChartContainerProps {
  cycleData: CycleData;
  selectedHormones: HormoneType[];
  showPhases: boolean;
}

export const HormoneChartContainer: React.FC<HormoneChartContainerProps> = ({
  cycleData,
  selectedHormones,
  showPhases
}) => {
  // Reference to the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Enable mouse wheel horizontal scrolling
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    
    if (!scrollContainer) return;
    
    const handleWheel = (evt: WheelEvent) => {
      // Only if the user is not pressing shift (which should scroll horizontally by default)
      if (!evt.shiftKey) {
        evt.preventDefault();
        scrollContainer.scrollLeft += evt.deltaY;
      }
    };
    
    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className="w-full relative" ref={scrollContainerRef}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute right-2 top-0 text-muted-foreground flex items-center text-xs cursor-help z-10">
              <ArrowLeftRight className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Scroll horizontally</span>
              <span className="sm:hidden">Scroll</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Swipe left/right or use scroll wheel to see the full chart</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <HormoneChartDisplay 
        cycleData={cycleData} 
        selectedHormones={selectedHormones}
        showPhases={showPhases}
      />
    </div>
  );
};
